// React in scope required for JSX with current tsconfig
import { useCallback, useMemo } from 'react';
import type { ErrorSchema } from '@rjsf/utils';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
/* eslint-disable-next-line -- reusing PlaceholderAwareOpenApiEditor from config-editor (intentional cross-plugin use) */
import { PlaceholderAwareOpenApiEditor } from 'config-editor';

type UiOptions = {
  rows?: number;
  knownPlaceholders?: string[];
  enforceTemplatizedDefinition?: boolean;
};

type Props = FieldExtensionComponentProps<string, UiOptions>;

function formDataToEditorText(formData: unknown): string {
  if (typeof formData === 'string') return formData;
  if (!formData) return '';
  return JSON.stringify(formData, null, 2);
}

/**
 * Scaffolder field wrapper for the OpenAPI JSON editor with placeholder
 * templatization. Delegates to config-editor's PlaceholderAwareOpenApiEditor.
 */
export const PlaceholderAwareOpenApi = (props: Props) => {
  const { onChange, schema, uiSchema, formData } = props;

  const value = formDataToEditorText(formData);
  const title = schema?.title || (uiSchema as any)?.['ui:title'] || 'OpenAPI JSON';
  const description = (schema?.description ||
    (uiSchema as any)?.['ui:description'] ||
    '') as string;
  const required = Boolean(props.required);
  const enforceTemplatizedDefinition =
    (uiSchema as any)?.['ui:options']?.enforceTemplatizedDefinition ?? true;
  const knownPlaceholders = useMemo(
    () => ((uiSchema as any)?.['ui:options']?.knownPlaceholders ?? []) as string[],
    [uiSchema],
  );

  const handleValidationChange = useCallback(
    (errs: string[]) => {
      const es: ErrorSchema<string> | undefined =
        errs.length > 0 ? ({ __errors: errs } as ErrorSchema<string>) : undefined;
      onChange(value, es as any);
    },
    [onChange, value],
  );

  return (
    <PlaceholderAwareOpenApiEditor
      value={value}
      onChange={v => onChange(v)}
      onValidationChange={handleValidationChange}
      title={title}
      description={description}
      required={required}
      knownPlaceholders={knownPlaceholders}
      enforceTemplatizedDefinition={enforceTemplatizedDefinition}
    />
  );
};
